import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckCircleIcon, XCircleIcon, ChevronDownIcon, ShieldCheckIcon, HeadphonesIcon, BookOpenIcon, ShoppingCartIcon, MoonIcon, ZapIcon, LockIcon, StarIcon } from 'lucide-react';
import { LegalModal } from '../components/LegalModal';
// ─── Reusable animation wrapper ───────────────────────────────────────────────
function FadeInUp({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 32
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.65,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} className={className} data-id="element-13">
      {children}
    </motion.div>;
}
// ─── CTA Button ───────────────────────────────────────────────────────────────
function CTAButton({
  size = 'lg',
  scrollOnly = false
}: {
  size?: 'lg' | 'xl';
  scrollOnly?: boolean;
}) {
  const sizeClasses = size === 'xl' ? 'text-xl md:text-2xl px-10 py-6' : 'text-lg md:text-xl px-8 py-5';
  if (scrollOnly) {
    return <a href="#oferta" className={`cta-button inline-block text-white rounded-full shadow-2xl cursor-pointer ${sizeClasses}`} aria-label="Ver precio del Recetario Vitalidad" data-id="element-14">
        🛒 ¡SÍ, QUIERO MI RECETARIO VITALIDAD AHORA!
      </a>;
  }
  return <a href="https://vivemejornow.com/cart/50933609496882:1" target="_blank" rel="noopener noreferrer" className={`cta-button inline-block text-white rounded-full shadow-2xl cursor-pointer ${sizeClasses}`} aria-label="Comprar Recetario Vitalidad ahora" data-id="element-15">
      🛒 ¡SÍ, QUIERO MI RECETARIO VITALIDAD AHORA!
    </a>;
}
// ─── FAQ Item ─────────────────────────────────────────────────────────────────
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [open, setOpen] = useState(false);
  return <div className="border-b border-amber-200 last:border-0" data-id="element-16">
      <button onClick={() => setOpen(!open)} className="w-full flex justify-between items-center py-5 text-left gap-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded" aria-expanded={open} data-id="element-17">
        <span style={{
        fontFamily: "'Playfair Display', serif",
        fontSize: '1.15rem',
        color: '#2D1B0E',
        fontWeight: 600
      }} data-id="element-18">
          {question}
        </span>
        <motion.div animate={{
        rotate: open ? 180 : 0
      }} transition={{
        duration: 0.3
      }} className="flex-shrink-0" data-id="element-19">
          <ChevronDownIcon className="w-6 h-6 text-amber-600" data-id="element-20" />
        </motion.div>
      </button>
      <motion.div initial={false} animate={{
      height: open ? 'auto' : 0,
      opacity: open ? 1 : 0
    }} transition={{
      duration: 0.35,
      ease: 'easeInOut'
    }} style={{
      overflow: 'hidden'
    }} data-id="element-21">
        <p className="pb-5 text-lg leading-relaxed" style={{
        color: '#5C3D1E'
      }} data-id="element-22">
          {answer}
        </p>
      </motion.div>
    </div>;
}
// ─── Main Landing Page ────────────────────────────────────────────────────────
export function LandingPage() {
  const [openModal, setOpenModal] = useState<null | 'privacy' | 'terms'>(null);
  return <main className="w-full overflow-x-hidden" style={{
    backgroundColor: '#FDF6EC',
    color: '#2D1B0E'
  }} data-id="element-23">
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="w-full py-16 md:py-24 px-4" style={{
      background: 'linear-gradient(160deg, #FDF6EC 0%, #FFF3E6 60%, #FDEBD0 100%)'
    }} data-id="element-24">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-25">
          {/* Left: copy */}
          <div className="flex flex-col gap-6" data-id="element-26">
            <motion.div initial={{
            opacity: 0,
            y: -16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-27">
              <span className="inline-block px-4 py-2 rounded-full text-sm font-bold tracking-wide" style={{
              background: 'linear-gradient(135deg, #D4A017, #E8890C)',
              color: '#fff'
            }} data-id="element-28">
                🌿 Método Natural Anti-Inflamatorio
              </span>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 28
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.1
          }} style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: 900,
            lineHeight: 1.2,
            color: '#2D1B0E'
          }} data-id="element-29">
              Vuelve a jugar con tus nietos sin que el dolor de rodillas te
              detenga
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} style={{
            fontSize: '1.25rem',
            lineHeight: 1.7,
            color: '#C4622D',
            fontWeight: 400
          }} data-id="element-30">
              El método natural y sabroso para apagar la inflamación articular
              en 14 días, usando los secretos de la cocina latina que ya tienes
              en casa.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.35
          }} className="flex flex-col items-start gap-3" data-id="element-31">
              <CTAButton size="xl" scrollOnly data-id="element-32" />
              <p className="text-base" style={{
              color: '#5C3D1E'
            }} data-id="element-33">
                🔒 Pago seguro · Acceso instantáneo
              </p>
            </motion.div>

            {/* Mini trust row */}
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.7,
            delay: 0.5
          }} className="flex flex-wrap gap-4 mt-2" data-id="element-34">
              <span className="flex items-center gap-1 text-sm" style={{
              color: '#5C3D1E'
            }} data-id="element-35">
                <ShieldCheckIcon className="w-4 h-4 text-green-600" data-id="element-36" /> Pago 100%
                seguro
              </span>
              <span className="flex items-center gap-1 text-sm" style={{
              color: '#5C3D1E'
            }} data-id="element-37">
                <ZapIcon className="w-4 h-4 text-amber-500" data-id="element-38" /> Descarga
                inmediata
              </span>
              <span className="flex items-center gap-1 text-sm" style={{
              color: '#5C3D1E'
            }} data-id="element-39">
                <StarIcon className="w-4 h-4 text-amber-500" data-id="element-40" /> Satisfacción
                garantizada
              </span>
            </motion.div>
          </div>

          {/* Right: product image */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.92
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} className="flex justify-center" data-id="element-41">
            <img src="https://cdn.magicpatterns.com/uploads/18yFkTDuHHrakrgMu214VW/proyecto_1.png" alt="Recetario Vitalidad — audiolibro y guía PDF en tableta" className="w-full max-w-md rounded-2xl shadow-2xl" style={{
            filter: 'drop-shadow(0 20px 60px rgba(196,98,45,0.25))'
          }} data-id="element-42" />
          </motion.div>
        </div>
      </section>

      {/* ── OPENING HOOK ─────────────────────────────────────────────────── */}
      <section className="w-full py-16 px-4" style={{
      backgroundColor: '#FDF6EC'
    }} data-id="element-43">
        <div className="max-w-3xl mx-auto text-center" data-id="element-44">
          <FadeInUp data-id="element-45">
            <div className="section-divider" data-id="element-46" />
            <blockquote style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(1.3rem, 3vw, 1.7rem)',
            fontStyle: 'italic',
            lineHeight: 1.6,
            color: '#2D1B0E',
            marginBottom: '1.5rem'
          }} data-id="element-47">
              "¿Recuerdas la última vez que te levantaste de la cama sin sentir
              ese pinchazo agudo en las rodillas o la rigidez en las manos?"
            </blockquote>
          </FadeInUp>
          <FadeInUp delay={0.1} data-id="element-48">
            <p className="text-lg leading-relaxed" style={{
            color: '#5C3D1E'
          }} data-id="element-49">
              Para muchas de nosotras, el café de la mañana viene acompañado de
              una pastilla para el dolor que solo <em data-id="element-50">"tapa"</em> el problema
              por unas horas. Pero el dolor no es solo{' '}
              <em data-id="element-51">"cosa de la edad"</em>. Es el resultado de una inflamación
              silenciosa que la comida procesada de Estados Unidos está
              alimentando cada vez que te sientas a la mesa.
            </p>
          </FadeInUp>
        </div>
      </section>

      {/* ── THE PROBLEM ──────────────────────────────────────────────────── */}
      <section className="w-full py-16 px-4" style={{
      backgroundColor: '#FFF3E6'
    }} data-id="element-52">
        <div className="max-w-3xl mx-auto" data-id="element-53">
          <FadeInUp data-id="element-54">
            <div className="section-divider" data-id="element-55" />
            <h2 className="text-center mb-8" style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)',
            fontWeight: 700,
            color: '#2D1B0E'
          }} data-id="element-56">
              Vivir en USA nos cambió la vida… y también la alimentación
            </h2>
          </FadeInUp>
          <FadeInUp delay={0.1} data-id="element-57">
            <p className="text-lg leading-relaxed mb-6" style={{
            color: '#5C3D1E'
          }} data-id="element-58">
              Los aceites vegetales, el pan blanco de caja y los azúcares
              ocultos están actuando como gasolina en el fuego de tus
              articulaciones. Esa hinchazón en los tobillos al final del día y
              la falta de fuerza al abrir un frasco no son normales.
            </p>
          </FadeInUp>
          <FadeInUp delay={0.2} data-id="element-59">
            <div className="rounded-2xl p-6 md:p-8 border-l-4" style={{
            backgroundColor: '#FDEBD0',
            borderColor: '#C4622D'
          }} data-id="element-60">
              <p className="text-xl font-bold" style={{
              fontFamily: "'Playfair Display', serif",
              color: '#C4622D'
            }} data-id="element-61">
                🔥 Tu cuerpo está gritando por ayuda, y las pastillas solo
                silencian el grito… pero no apagan el incendio.
              </p>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* ── WHY WHAT YOU TRIED DIDN'T WORK ───────────────────────────────── */}
      <section className="w-full py-16 px-4" style={{
      backgroundColor: '#FDF6EC'
    }} data-id="element-62">
        <div className="max-w-3xl mx-auto" data-id="element-63">
          <FadeInUp data-id="element-64">
            <div className="section-divider" data-id="element-65" />
            <h2 className="text-center mb-10" style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)',
            fontWeight: 700,
            color: '#2D1B0E'
          }} data-id="element-66">
              ¿Por qué lo que intentaste antes no funcionó?
            </h2>
          </FadeInUp>

          <div className="flex flex-col gap-5" data-id="element-67">
            <FadeInUp delay={0.05} data-id="element-68">
              <div className="flex items-start gap-4 p-5 rounded-xl" style={{
              backgroundColor: '#FFF3E6'
            }} data-id="element-69">
                <XCircleIcon className="w-7 h-7 flex-shrink-0 mt-1" style={{
                color: '#C4622D'
              }} data-id="element-70" />
                <p className="text-lg leading-relaxed" style={{
                color: '#5C3D1E'
              }} data-id="element-71">
                  <strong style={{
                  color: '#2D1B0E'
                }} data-id="element-72">
                    Las dietas americanas de "ensalada y pollo insípido"
                  </strong>{' '}
                  no funcionan para nosotras porque extrañamos nuestro sazón.
                </p>
              </div>
            </FadeInUp>
            <FadeInUp delay={0.1} data-id="element-73">
              <div className="flex items-start gap-4 p-5 rounded-xl" style={{
              backgroundColor: '#FFF3E6'
            }} data-id="element-74">
                <XCircleIcon className="w-7 h-7 flex-shrink-0 mt-1" style={{
                color: '#C4622D'
              }} data-id="element-75" />
                <p className="text-lg leading-relaxed" style={{
                color: '#5C3D1E'
              }} data-id="element-76">
                  <strong style={{
                  color: '#2D1B0E'
                }} data-id="element-77">
                    Los suplementos caros de la farmacia
                  </strong>{' '}
                  no pueden competir contra 3 comidas diarias cargadas de
                  ingredientes inflamatorios.
                </p>
              </div>
            </FadeInUp>
            <FadeInUp delay={0.15} data-id="element-78">
              <div className="flex items-start gap-4 p-5 rounded-xl" style={{
              backgroundColor: '#FFF3E6'
            }} data-id="element-79">
                <XCircleIcon className="w-7 h-7 flex-shrink-0 mt-1" style={{
                color: '#C4622D'
              }} data-id="element-80" />
                <p className="text-lg leading-relaxed" style={{
                color: '#5C3D1E'
              }} data-id="element-81">
                  <strong style={{
                  color: '#2D1B0E'
                }} data-id="element-82">
                    Los consejos genéricos de "haga ejercicio"
                  </strong>{' '}
                  son imposibles de seguir cuando cada paso que das te causa un
                  dolor punzante.
                </p>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* ── SOFRITO DE ORO ───────────────────────────────────────────────── */}
      <section className="w-full py-16 px-4" style={{
      background: 'linear-gradient(135deg, #2D1B0E 0%, #5C3D1E 100%)'
    }} data-id="element-83">
        <div className="max-w-3xl mx-auto text-center" data-id="element-84">
          <FadeInUp data-id="element-85">
            <span className="sofrito-badge inline-block px-5 py-2 rounded-full text-white text-sm font-bold tracking-widest uppercase mb-6" data-id="element-86">
              ✨ El Secreto
            </span>
            <h2 className="mb-6" style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
            fontWeight: 900,
            color: '#D4A017',
            lineHeight: 1.2
          }} data-id="element-87">
              Presentamos el "Sofrito de Oro"
            </h2>
          </FadeInUp>
          <FadeInUp delay={0.1} data-id="element-88">
            <p className="text-lg leading-relaxed mb-6" style={{
            color: '#FFF3E6'
          }} data-id="element-89">
              Nuestra técnica exclusiva para transformar tus platos latinos
              favoritos en medicina natural. Al combinar ingredientes
              tradicionales como el{' '}
              <strong style={{
              color: '#D4A017'
            }} data-id="element-90">
                ajo, la cebolla y la cúrcuma
              </strong>{' '}
              con una pizca de pimienta negra, creamos un escudo protector para
              tus cartílagos…
            </p>
          </FadeInUp>
          <FadeInUp delay={0.2} data-id="element-91">
            <p className="text-2xl font-bold" style={{
            fontFamily: "'Playfair Display', serif",
            color: '#E8890C'
          }} data-id="element-92">
              …sin perder el sabor que amas. 🫶
            </p>
          </FadeInUp>
        </div>
      </section>

      {/* ── WHAT YOU GET ─────────────────────────────────────────────────── */}
      <section className="w-full py-16 px-4" style={{
      backgroundColor: '#FFF3E6'
    }} data-id="element-93">
        <div className="max-w-4xl mx-auto" data-id="element-94">
          <FadeInUp data-id="element-95">
            <div className="section-divider" data-id="element-96" />
            <h2 className="text-center mb-12" style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)',
            fontWeight: 700,
            color: '#2D1B0E'
          }} data-id="element-97">
              Todo lo que recibes hoy
            </h2>
          </FadeInUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-98">
            <FadeInUp delay={0.05} data-id="element-99">
              <div className="flex items-start gap-5 p-6 rounded-2xl shadow-md" style={{
              backgroundColor: '#FDF6EC',
              border: '2px solid #D4A017'
            }} data-id="element-100">
                <div className="flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center" style={{
                background: 'linear-gradient(135deg, #D4A017, #E8890C)'
              }} data-id="element-101">
                  <HeadphonesIcon className="w-7 h-7 text-white" data-id="element-102" />
                </div>
                <div data-id="element-103">
                  <h3 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: '#2D1B0E',
                  marginBottom: '0.4rem'
                }} data-id="element-104">
                    Audiolibro Recetario Vitalidad
                  </h3>
                  <p className="text-base leading-relaxed" style={{
                  color: '#5C3D1E'
                }} data-id="element-105">
                    Escúchame mientras cocinas o descansas; te guiaré paso a
                    paso con voz clara y cariñosa.
                  </p>
                </div>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.1} data-id="element-106">
              <div className="flex items-start gap-5 p-6 rounded-2xl shadow-md" style={{
              backgroundColor: '#FDF6EC',
              border: '2px solid #D4A017'
            }} data-id="element-107">
                <div className="flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center" style={{
                background: 'linear-gradient(135deg, #D4A017, #E8890C)'
              }} data-id="element-108">
                  <BookOpenIcon className="w-7 h-7 text-white" data-id="element-109" />
                </div>
                <div data-id="element-110">
                  <h3 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: '#2D1B0E',
                  marginBottom: '0.4rem'
                }} data-id="element-111">
                    Guía PDF de Referencia Rápida
                  </h3>
                  <p className="text-base leading-relaxed" style={{
                  color: '#5C3D1E'
                }} data-id="element-112">
                    Con letras grandes y claras para que no fuerces la vista.
                    Imprimible o en pantalla.
                  </p>
                </div>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.15} data-id="element-113">
              <div className="flex items-start gap-5 p-6 rounded-2xl shadow-md" style={{
              backgroundColor: '#FDF6EC',
              border: '2px solid #D4A017'
            }} data-id="element-114">
                <div className="flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center" style={{
                background: 'linear-gradient(135deg, #D4A017, #E8890C)'
              }} data-id="element-115">
                  <ShoppingCartIcon className="w-7 h-7 text-white" data-id="element-116" />
                </div>
                <div data-id="element-117">
                  <h3 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: '#2D1B0E',
                  marginBottom: '0.4rem'
                }} data-id="element-118">
                    El Semáforo del Supermercado
                  </h3>
                  <p className="text-base leading-relaxed" style={{
                  color: '#5C3D1E'
                }} data-id="element-119">
                    Para que sepas qué comprar y qué dejar en el estante de
                    Walmart, Sedano's o Publix.
                  </p>
                </div>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.2} data-id="element-120">
              <div className="flex items-start gap-5 p-6 rounded-2xl shadow-md" style={{
              backgroundColor: '#FDF6EC',
              border: '2px solid #D4A017'
            }} data-id="element-121">
                <div className="flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center" style={{
                background: 'linear-gradient(135deg, #D4A017, #E8890C)'
              }} data-id="element-122">
                  <MoonIcon className="w-7 h-7 text-white" data-id="element-123" />
                </div>
                <div data-id="element-124">
                  <h3 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: '#2D1B0E',
                  marginBottom: '0.4rem'
                }} data-id="element-125">
                    Protocolo del Té Nocturno
                  </h3>
                  <p className="text-base leading-relaxed" style={{
                  color: '#5C3D1E'
                }} data-id="element-126">
                    La infusión exacta para desinflamar tus articulaciones
                    mientras duermes plácidamente.
                  </p>
                </div>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* ── FOR WHO / NOT FOR WHO ────────────────────────────────────────── */}
      <section className="w-full py-16 px-4" style={{
      backgroundColor: '#FDF6EC'
    }} data-id="element-127">
        <div className="max-w-5xl mx-auto" data-id="element-128">
          <FadeInUp data-id="element-129">
            <div className="section-divider" data-id="element-130" />
            <h2 className="text-center mb-12" style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)',
            fontWeight: 700,
            color: '#2D1B0E'
          }} data-id="element-131">
              ¿Es esto para ti?
            </h2>
          </FadeInUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-132">
            {/* For */}
            <FadeInUp delay={0.05} data-id="element-133">
              <div className="rounded-2xl p-6 md:p-8" style={{
              backgroundColor: '#F0FBF0',
              border: '2px solid #6DBF6D'
            }} data-id="element-134">
                <h3 className="mb-5 text-center" style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: '1.4rem',
                fontWeight: 700,
                color: '#2D6B2D'
              }} data-id="element-135">
                  ✅ SÍ es para ti si…
                </h3>
                <ul className="flex flex-col gap-3" data-id="element-136">
                  <li className="flex items-start gap-3" data-id="element-137">
                    <CheckCircleIcon className="w-6 h-6 flex-shrink-0 mt-0.5" style={{
                    color: '#4CAF50'
                  }} data-id="element-138" />
                    <span className="text-lg" style={{
                    color: '#2D4A2D'
                  }} data-id="element-139">
                      Eres mujer de 65+ años cansada de sentir rigidez al
                      despertar.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-140">
                    <CheckCircleIcon className="w-6 h-6 flex-shrink-0 mt-0.5" style={{
                    color: '#4CAF50'
                  }} data-id="element-141" />
                    <span className="text-lg" style={{
                    color: '#2D4A2D'
                  }} data-id="element-142">
                      Quieres tener energía para cargar a tus nietos sin miedo a
                      caerte.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-143">
                    <CheckCircleIcon className="w-6 h-6 flex-shrink-0 mt-0.5" style={{
                    color: '#4CAF50'
                  }} data-id="element-144" />
                    <span className="text-lg" style={{
                    color: '#2D4A2D'
                  }} data-id="element-145">
                      Eres latina en USA que quiere comer sano sin dejar el
                      sazón hispano.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-146">
                    <CheckCircleIcon className="w-6 h-6 flex-shrink-0 mt-0.5" style={{
                    color: '#4CAF50'
                  }} data-id="element-147" />
                    <span className="text-lg" style={{
                    color: '#2D4A2D'
                  }} data-id="element-148">
                      Prefieres soluciones naturales antes que llenar tu
                      estómago de químicos.
                    </span>
                  </li>
                </ul>
              </div>
            </FadeInUp>

            {/* Not for */}
            <FadeInUp delay={0.1} data-id="element-149">
              <div className="rounded-2xl p-6 md:p-8" style={{
              backgroundColor: '#FFF5F5',
              border: '2px solid #F5A0A0'
            }} data-id="element-150">
                <h3 className="mb-5 text-center" style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: '1.4rem',
                fontWeight: 700,
                color: '#8B2020'
              }} data-id="element-151">
                  ❌ NO es para ti si…
                </h3>
                <ul className="flex flex-col gap-3" data-id="element-152">
                  <li className="flex items-start gap-3" data-id="element-153">
                    <XCircleIcon className="w-6 h-6 flex-shrink-0 mt-0.5" style={{
                    color: '#E53E3E'
                  }} data-id="element-154" />
                    <span className="text-lg" style={{
                    color: '#5C2020'
                  }} data-id="element-155">
                      Buscas una "cura milagrosa" de la noche a la mañana sin
                      cambiar lo que comes.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-156">
                    <XCircleIcon className="w-6 h-6 flex-shrink-0 mt-0.5" style={{
                    color: '#E53E3E'
                  }} data-id="element-157" />
                    <span className="text-lg" style={{
                    color: '#5C2020'
                  }} data-id="element-158">
                      No estás dispuesta a cambiar el aceite de maíz por
                      opciones más saludables.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-159">
                    <XCircleIcon className="w-6 h-6 flex-shrink-0 mt-0.5" style={{
                    color: '#E53E3E'
                  }} data-id="element-160" />
                    <span className="text-lg" style={{
                    color: '#5C2020'
                  }} data-id="element-161">
                      No sufres de dolores articulares ni inflamación.
                    </span>
                  </li>
                </ul>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────────── */}
      <section className="w-full py-16 px-4" style={{
      backgroundColor: '#FFF3E6'
    }} data-id="element-162">
        <div className="max-w-3xl mx-auto" data-id="element-163">
          <FadeInUp data-id="element-164">
            <div className="section-divider" data-id="element-165" />
            <h2 className="text-center mb-12" style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)',
            fontWeight: 700,
            color: '#2D1B0E'
          }} data-id="element-166">
              Así de fácil funciona
            </h2>
          </FadeInUp>

          <div className="flex flex-col gap-6" data-id="element-167">
            <FadeInUp delay={0.05} data-id="element-168">
              <div className="flex items-start gap-5" data-id="element-169">
                <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl" style={{
                background: 'linear-gradient(135deg, #E8890C, #C4622D)'
              }} data-id="element-170">
                  1
                </div>
                <div className="pt-1" data-id="element-171">
                  <h3 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: '#2D1B0E',
                  marginBottom: '0.3rem'
                }} data-id="element-172">
                    Descarga el audio y la guía
                  </h3>
                  <p className="text-lg" style={{
                  color: '#5C3D1E'
                }} data-id="element-173">
                    En tu celular o tableta inmediatamente después de tu compra,
                    sin complicaciones.
                  </p>
                </div>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.1} data-id="element-174">
              <div className="flex items-start gap-5" data-id="element-175">
                <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl" style={{
                background: 'linear-gradient(135deg, #E8890C, #C4622D)'
              }} data-id="element-176">
                  2
                </div>
                <div className="pt-1" data-id="element-177">
                  <h3 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: '#2D1B0E',
                  marginBottom: '0.3rem'
                }} data-id="element-178">
                    Escucha la Pista #1
                  </h3>
                  <p className="text-lg" style={{
                  color: '#5C3D1E'
                }} data-id="element-179">
                    Entiende qué está causando tu dolor hoy mismo. Fácil como
                    escuchar un mensaje de WhatsApp.
                  </p>
                </div>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.15} data-id="element-180">
              <div className="flex items-start gap-5" data-id="element-181">
                <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl" style={{
                background: 'linear-gradient(135deg, #E8890C, #C4622D)'
              }} data-id="element-182">
                  3
                </div>
                <div className="pt-1" data-id="element-183">
                  <h3 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: '#2D1B0E',
                  marginBottom: '0.3rem'
                }} data-id="element-184">
                    Aplica el "Sofrito de Oro" hoy
                  </h3>
                  <p className="text-lg" style={{
                  color: '#5C3D1E'
                }} data-id="element-185">
                    En tu próximo almuerzo y siente la diferencia en tus niveles
                    de energía desde el primer día.
                  </p>
                </div>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* ── PROOF / BELIEVABILITY ────────────────────────────────────────── */}
      <section className="w-full py-16 px-4" style={{
      background: 'linear-gradient(135deg, #FDF6EC 0%, #FFF3E6 100%)'
    }} data-id="element-186">
        <div className="max-w-3xl mx-auto text-center" data-id="element-187">
          <FadeInUp data-id="element-188">
            <div className="section-divider" data-id="element-189" />
            <h2 className="mb-8" style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)',
            fontWeight: 700,
            color: '#2D1B0E'
          }} data-id="element-190">
              Lo que notarás en los primeros 7 días
            </h2>
          </FadeInUp>
          <FadeInUp delay={0.1} data-id="element-191">
            <div className="rounded-2xl p-8 md:p-10 shadow-lg" style={{
            backgroundColor: '#FDF6EC',
            border: '2px solid #D4A017'
          }} data-id="element-192">
              <p className="text-xl leading-relaxed mb-6" style={{
              fontFamily: "'Playfair Display', serif",
              fontStyle: 'italic',
              color: '#2D1B0E'
            }} data-id="element-193">
                "En solo 7 días, notarás que la hinchazón de tus tobillos
                disminuye y que tus dedos recuperan la agilidad necesaria para
                sus labores diarias."
              </p>
              <p className="text-lg" style={{
              color: '#5C3D1E'
            }} data-id="element-194">
                No es magia. Es ciencia aplicada a nuestra cultura. 🧬🫚
              </p>
              <div className="flex justify-center gap-1 mt-4" data-id="element-195">
                <StarIcon className="w-6 h-6 fill-current" style={{
                color: '#D4A017'
              }} data-id="element-196" />
                <StarIcon className="w-6 h-6 fill-current" style={{
                color: '#D4A017'
              }} data-id="element-197" />
                <StarIcon className="w-6 h-6 fill-current" style={{
                color: '#D4A017'
              }} data-id="element-198" />
                <StarIcon className="w-6 h-6 fill-current" style={{
                color: '#D4A017'
              }} data-id="element-199" />
                <StarIcon className="w-6 h-6 fill-current" style={{
                color: '#D4A017'
              }} data-id="element-200" />
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="w-full py-16 px-4" style={{
      backgroundColor: '#FFF3E6'
    }} data-id="element-201">
        <div className="max-w-3xl mx-auto" data-id="element-202">
          <FadeInUp data-id="element-203">
            <div className="section-divider" data-id="element-204" />
            <h2 className="text-center mb-10" style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)',
            fontWeight: 700,
            color: '#2D1B0E'
          }} data-id="element-205">
              Preguntas frecuentes
            </h2>
          </FadeInUp>
          <FadeInUp delay={0.1} data-id="element-206">
            <div className="rounded-2xl p-6 md:p-8 shadow-md" style={{
            backgroundColor: '#FDF6EC'
          }} data-id="element-207">
              <FAQItem question="¿Tengo que dejar de comer mi comida latina?" answer="Para nada. Aprenderás a prepararla de forma que te sane en lugar de inflamarte. El sazón, el sofrito, el arroz con frijoles — todo se queda. Solo cambiamos cómo los preparamos." data-id="element-208" />
              <FAQItem question="¿Es difícil conseguir los ingredientes en USA?" answer="Para nada. Todos se encuentran en cualquier supermercado común como Sedano's, Bravo o Walmart. Nada exótico ni caro." data-id="element-209" />
              <FAQItem question="¿Puedo escucharlo si no sé mucho de tecnología?" answer="Sí, el acceso es un solo clic y el audio se reproduce como un mensaje de WhatsApp. Si sabes abrir un mensaje de voz, sabes usar esto." data-id="element-210" />
              <FAQItem question="¿Necesito equipo especial de cocina?" answer="Solo tus ollas de siempre y muchas ganas de sentirte bien. Nada de gadgets ni aparatos especiales." data-id="element-211" />
              <FAQItem question="¿En cuánto tiempo veré resultados?" answer="La mayoría de las mujeres reportan sentir más energía y menos hinchazón en los primeros 7 días. Los resultados completos se notan en 14 días de seguir el método." data-id="element-212" />
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* ── OFFER + PRICE + FINAL CTA ────────────────────────────────────── */}
      <section id="oferta" className="w-full py-20 px-4" style={{
      background: 'linear-gradient(160deg, #2D1B0E 0%, #5C3D1E 50%, #2D1B0E 100%)'
    }} data-id="element-213">
        <div className="max-w-2xl mx-auto text-center" data-id="element-214">
          <FadeInUp data-id="element-215">
            <span className="sofrito-badge inline-block px-5 py-2 rounded-full text-white text-sm font-bold tracking-widest uppercase mb-6" data-id="element-216">
              🎉 Precio Especial de Lanzamiento
            </span>
            <h2 className="mb-4" style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
            fontWeight: 900,
            color: '#FDF6EC',
            lineHeight: 1.2
          }} data-id="element-217">
              Recupera tu movilidad hoy por menos de lo que cuesta una salida a
              comer fuera
            </h2>
          </FadeInUp>

          <FadeInUp delay={0.1} data-id="element-218">
            <p className="text-lg mb-8" style={{
            color: '#FDEBD0'
          }} data-id="element-219">
              Invierte en tus años dorados y deja de permitir que el dolor
              decida qué puedes hacer y qué no.
            </p>
          </FadeInUp>

          <FadeInUp delay={0.15} data-id="element-220">
            <div className="rounded-3xl p-8 md:p-12 mb-8 shadow-2xl" style={{
            backgroundColor: '#FDF6EC'
          }} data-id="element-221">
              {/* What's included summary */}
              <p className="text-sm font-bold tracking-widest uppercase mb-4" style={{
              color: '#C4622D'
            }} data-id="element-222">
                Todo incluido:
              </p>
              <ul className="flex flex-col gap-2 mb-8 text-left max-w-sm mx-auto" data-id="element-223">
                <li className="flex items-center gap-3 text-lg" style={{
                color: '#2D1B0E'
              }} data-id="element-224">
                  <CheckCircleIcon className="w-5 h-5 flex-shrink-0" style={{
                  color: '#4CAF50'
                }} data-id="element-225" />
                  Audiolibro Recetario Vitalidad
                </li>
                <li className="flex items-center gap-3 text-lg" style={{
                color: '#2D1B0E'
              }} data-id="element-226">
                  <CheckCircleIcon className="w-5 h-5 flex-shrink-0" style={{
                  color: '#4CAF50'
                }} data-id="element-227" />
                  Guía PDF de Referencia Rápida
                </li>
                <li className="flex items-center gap-3 text-lg" style={{
                color: '#2D1B0E'
              }} data-id="element-228">
                  <CheckCircleIcon className="w-5 h-5 flex-shrink-0" style={{
                  color: '#4CAF50'
                }} data-id="element-229" />
                  El Semáforo del Supermercado
                </li>
                <li className="flex items-center gap-3 text-lg" style={{
                color: '#2D1B0E'
              }} data-id="element-230">
                  <CheckCircleIcon className="w-5 h-5 flex-shrink-0" style={{
                  color: '#4CAF50'
                }} data-id="element-231" />
                  Protocolo del Té Nocturno
                </li>
              </ul>

              {/* Price */}
              <div className="mb-8" data-id="element-232">
                <p className="text-base line-through mb-1" style={{
                color: '#999'
              }} data-id="element-233">
                  Valor regular: $47.00 USD
                </p>
                <p className="text-base line-through mb-1" style={{
                color: '#C4622D',
                opacity: 0.7
              }} data-id="element-234">
                  Precio especial: $19.00 USD
                </p>
                <div className="inline-block px-3 py-1 rounded-full text-sm font-bold mb-2" style={{
                background: 'linear-gradient(135deg, #D4A017, #E8890C)',
                color: '#fff'
              }} data-id="element-235">
                  🔥 Descuento de lanzamiento — ¡Solo por tiempo limitado!
                </div>
                <p style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(3rem, 8vw, 5rem)',
                fontWeight: 900,
                color: '#C4622D',
                lineHeight: 1
              }} data-id="element-236">
                  $9.99
                  <span className="text-2xl font-normal ml-2" style={{
                  color: '#5C3D1E'
                }} data-id="element-237">
                    USD
                  </span>
                </p>
                <p className="text-base mt-1" style={{
                color: '#5C3D1E'
              }} data-id="element-238">
                  Pago único · Sin suscripción
                </p>
              </div>

              <CTAButton size="xl" data-id="element-239" />

              <p className="mt-4 text-base" style={{
              color: '#5C3D1E'
            }} data-id="element-240">
                🔒 Pago seguro y acceso instantáneo incluso si son las 2:00 AM
              </p>

              {/* Trust badges */}
              <div className="flex flex-wrap justify-center gap-4 mt-6 pt-6 border-t border-amber-200" data-id="element-241">
                <span className="flex items-center gap-1 text-sm" style={{
                color: '#5C3D1E'
              }} data-id="element-242">
                  <ShieldCheckIcon className="w-4 h-4 text-green-600" data-id="element-243" /> SSL
                  Seguro
                </span>
                <span className="flex items-center gap-1 text-sm" style={{
                color: '#5C3D1E'
              }} data-id="element-244">
                  <LockIcon className="w-4 h-4 text-amber-600" data-id="element-245" /> Datos
                  protegidos
                </span>
                <span className="flex items-center gap-1 text-sm" style={{
                color: '#5C3D1E'
              }} data-id="element-246">
                  <ZapIcon className="w-4 h-4 text-amber-500" data-id="element-247" /> Descarga
                  inmediata
                </span>
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────────────── */}
      <footer className="w-full py-8 px-4 text-center" style={{
      backgroundColor: '#2D1B0E'
    }} data-id="element-248">
        <p className="text-sm" style={{
        color: '#FDEBD0',
        opacity: 0.7
      }} data-id="element-249">
          © 2025 Recetario Vitalidad · Todos los derechos reservados
        </p>
        <p className="text-xs mt-2" style={{
        color: '#FDEBD0',
        opacity: 0.5
      }} data-id="element-250">
          Este producto es de carácter educativo y no reemplaza el consejo
          médico profesional.
        </p>
        <div className="flex items-center justify-center gap-4 mt-3" data-id="element-251">
          <button onClick={() => setOpenModal('privacy')} className="text-xs underline-offset-2 hover:underline transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400 rounded" style={{
          color: '#D4A017',
          opacity: 0.75,
          fontFamily: "'Lato', sans-serif"
        }} data-id="element-252">
            Política de Privacidad
          </button>
          <span style={{
          color: '#FDEBD0',
          opacity: 0.3
        }} data-id="element-253">
            ·
          </span>
          <button onClick={() => setOpenModal('terms')} className="text-xs underline-offset-2 hover:underline transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400 rounded" style={{
          color: '#D4A017',
          opacity: 0.75,
          fontFamily: "'Lato', sans-serif"
        }} data-id="element-254">
            Términos y Condiciones
          </button>
        </div>
      </footer>

      {/* ── PRIVACY POLICY MODAL ─────────────────────────────────────────── */}
      <LegalModal isOpen={openModal === 'privacy'} onClose={() => setOpenModal(null)} title="Política de Privacidad" data-id="element-255">
        <div className="space-y-5" data-id="element-256">
          <section data-id="element-257">
            <h3 style={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
            color: '#2D1B0E',
            fontSize: '1.05rem',
            marginBottom: '0.4rem'
          }} data-id="element-258">
              1. Información que Recopilamos
            </h3>
            <p data-id="element-259">
              Al realizar una compra, recopilamos tu dirección de correo
              electrónico y los datos necesarios para procesar el pago (nombre,
              información de tarjeta de crédito o débito). Esta información es
              gestionada de forma segura por nuestro procesador de pagos
              certificado.
            </p>
          </section>

          <section data-id="element-260">
            <h3 style={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
            color: '#2D1B0E',
            fontSize: '1.05rem',
            marginBottom: '0.4rem'
          }} data-id="element-261">
              2. Uso de la Información
            </h3>
            <p data-id="element-262">
              Utilizamos tu información exclusivamente para entregar el producto
              digital adquirido y procesar tu pago de forma segura. No
              enviaremos correos electrónicos no solicitados ni utilizaremos tus
              datos con fines de mercadeo sin tu consentimiento expreso.
            </p>
          </section>

          <section data-id="element-263">
            <h3 style={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
            color: '#2D1B0E',
            fontSize: '1.05rem',
            marginBottom: '0.4rem'
          }} data-id="element-264">
              3. Compartir Información
            </h3>
            <p data-id="element-265">
              No vendemos, alquilamos ni compartimos tu información personal con
              terceros. Tus datos son confidenciales y solo son accesibles por
              nuestro equipo y los proveedores de servicios estrictamente
              necesarios para completar tu compra (como el procesador de pagos).
            </p>
          </section>

          <section data-id="element-266">
            <h3 style={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
            color: '#2D1B0E',
            fontSize: '1.05rem',
            marginBottom: '0.4rem'
          }} data-id="element-267">
              4. Seguridad
            </h3>
            <p data-id="element-268">
              Todas las transacciones se realizan a través de conexiones
              cifradas con tecnología SSL. El procesamiento de pagos cumple con
              los estándares de seguridad PCI-DSS para proteger tu información
              financiera en todo momento.
            </p>
          </section>

          <section data-id="element-269">
            <h3 style={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
            color: '#2D1B0E',
            fontSize: '1.05rem',
            marginBottom: '0.4rem'
          }} data-id="element-270">
              5. Cookies
            </h3>
            <p data-id="element-271">
              Este sitio utiliza cookies básicas de análisis para entender cómo
              los visitantes interactúan con la página. No utilizamos cookies de
              seguimiento publicitario ni compartimos esta información con redes
              de publicidad externas.
            </p>
          </section>

          <section data-id="element-272">
            <h3 style={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
            color: '#2D1B0E',
            fontSize: '1.05rem',
            marginBottom: '0.4rem'
          }} data-id="element-273">
              6. Sus Derechos
            </h3>
            <p data-id="element-274">
              Tienes derecho a solicitar la eliminación de tus datos personales
              de nuestros registros en cualquier momento. Para ejercer este
              derecho, puedes enviarnos una solicitud y procesaremos tu petición
              a la brevedad posible.
            </p>
          </section>

          <section data-id="element-275">
            <h3 style={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
            color: '#2D1B0E',
            fontSize: '1.05rem',
            marginBottom: '0.4rem'
          }} data-id="element-276">
              7. Cambios a esta Política
            </h3>
            <p data-id="element-277">
              Nos reservamos el derecho de actualizar esta Política de
              Privacidad en cualquier momento. Cualquier cambio significativo
              será comunicado a través de este sitio web. Te recomendamos
              revisar esta página periódicamente.
            </p>
          </section>
        </div>
      </LegalModal>

      {/* ── TERMS OF SERVICE MODAL ───────────────────────────────────────── */}
      <LegalModal isOpen={openModal === 'terms'} onClose={() => setOpenModal(null)} title="Términos y Condiciones" data-id="element-278">
        <div className="space-y-5" data-id="element-279">
          <section data-id="element-280">
            <h3 style={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
            color: '#2D1B0E',
            fontSize: '1.05rem',
            marginBottom: '0.4rem'
          }} data-id="element-281">
              1. Aceptación de los Términos
            </h3>
            <p data-id="element-282">
              Al realizar una compra en este sitio, aceptas de forma expresa
              estos Términos y Condiciones en su totalidad. Si no estás de
              acuerdo con alguno de los términos aquí establecidos, te pedimos
              que no realices la compra.
            </p>
          </section>

          <section data-id="element-283">
            <h3 style={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
            color: '#2D1B0E',
            fontSize: '1.05rem',
            marginBottom: '0.4rem'
          }} data-id="element-284">
              2. Descripción del Producto
            </h3>
            <p data-id="element-285">
              Recetario Vitalidad es un producto digital de carácter educativo
              que incluye un audiolibro y una guía en formato PDF. Su contenido
              está diseñado con fines informativos y de bienestar general, y{' '}
              <strong data-id="element-286">
                no constituye consejo médico, diagnóstico ni tratamiento
              </strong>{' '}
              de ninguna condición de salud.
            </p>
          </section>

          <section data-id="element-287">
            <h3 style={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
            color: '#2D1B0E',
            fontSize: '1.05rem',
            marginBottom: '0.4rem'
          }} data-id="element-288">
              3. Acceso y Descarga
            </h3>
            <p data-id="element-289">
              Una vez confirmado el pago, recibirás acceso inmediato al producto
              digital. El producto es de uso personal y no está permitida su
              reproducción, distribución, reventa ni compartición con terceros
              bajo ninguna circunstancia. El acceso es individual e
              intransferible.
            </p>
          </section>

          <section data-id="element-290">
            <h3 style={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
            color: '#2D1B0E',
            fontSize: '1.05rem',
            marginBottom: '0.4rem'
          }} data-id="element-291">
              4. Política de Reembolso
            </h3>
            <p data-id="element-292">
              Ofrecemos una garantía de satisfacción de 30 días. Si por
              cualquier razón no estás satisfecha con el producto, puedes
              solicitar un reembolso completo dentro de los 30 días posteriores
              a tu compra enviando una solicitud. Procesaremos tu reembolso sin
              preguntas.
            </p>
          </section>

          <section data-id="element-293">
            <h3 style={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
            color: '#2D1B0E',
            fontSize: '1.05rem',
            marginBottom: '0.4rem'
          }} data-id="element-294">
              5. Propiedad Intelectual
            </h3>
            <p data-id="element-295">
              Todo el contenido de Recetario Vitalidad, incluyendo textos,
              audios, imágenes y diseños, está protegido por derechos de autor.
              Queda estrictamente prohibida su reproducción total o parcial,
              distribución o modificación sin autorización escrita expresa.
            </p>
          </section>

          <section data-id="element-296">
            <h3 style={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
            color: '#2D1B0E',
            fontSize: '1.05rem',
            marginBottom: '0.4rem'
          }} data-id="element-297">
              6. Limitación de Responsabilidad
            </h3>
            <p data-id="element-298">
              Este producto tiene exclusivamente fines educativos e
              informativos. No somos responsables de los resultados individuales
              que pueda experimentar cada persona. Los resultados pueden variar.
              Consulta siempre a un profesional de la salud antes de realizar
              cambios significativos en tu dieta o estilo de vida.
            </p>
          </section>

          <section data-id="element-299">
            <h3 style={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
            color: '#2D1B0E',
            fontSize: '1.05rem',
            marginBottom: '0.4rem'
          }} data-id="element-300">
              7. Ley Aplicable
            </h3>
            <p data-id="element-301">
              Estos Términos y Condiciones se rigen por las leyes aplicables en
              la jurisdicción correspondiente. Cualquier disputa que surja en
              relación con estos términos será resuelta de conformidad con
              dichas leyes y en los tribunales competentes.
            </p>
          </section>
        </div>
      </LegalModal>
    </main>;
}